import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, switchMap, take, throwError } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { SessionStateService } from '../services/session-state.service';

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const auth         = inject(AuthService);
  const sessionState = inject(SessionStateService);

  // Only send credentials for same-origin API requests, not external URLs.
  const modifiedReq = req.url.startsWith('/')
    ? req.clone({ withCredentials: true })
    : req;

  return next(modifiedReq).pipe(
    catchError((err: HttpErrorResponse) => {
      if (err.status !== 401) return throwError(() => err);

      // Auth endpoints (login, verify, register, resend-code) return 401 for
      // wrong credentials / bad codes — that is NOT a session expiry. Likewise,
      // if no user was ever logged in there is no session to expire.
      // In both cases, propagate the error so the component's own handler
      // can show the appropriate inline message.
      const isAuthEndpoint = req.url.includes('/api/v1/auth/');
      if (isAuthEndpoint || !auth.currentUser()) return throwError(() => err);

      // Clear the current user signal immediately so guards/UI react.
      auth.handleUnauthorized();

      // Lock the app and snapshot unsaved state (idempotent if already locked).
      sessionState.onUnauthorized();

      // Wait for the re-auth flow to complete, then either retry or propagate.
      return sessionState.authResult$.pipe(
        take(1),
        switchMap(result =>
          result === 'restored'
            ? next(modifiedReq)
            : throwError(() => err)
        ),
      );
    }),
  );
};
